import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { Accessor } from "IZOX/STATIC";
import { HStack, XInput } from "IZOX/_X";

/**
 * @typedef {import("IZOX/_X/XInput").xinputparams} xinputparams
 * @typedef {import("IZOX/_X/XInput").xinputstyleparams} xinputstyleparams
 * @typedef {import("./QFText").qftextparams} qftextparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * 
 * @typedef {Object} qfdigitboxparams
 * @property {Number} digit
 * @property {Number} boxWidth
 * @property {Number} boxHeight
 * @property {Number} spacing
 * @property {(value) => void} onDigitEnd
 * 
 * @param {qfdigitboxparams & xinputparams
 *  & xinputstyleparams & qftextparams} param0 
 * @returns 
 */
function QFDigitBox({
  digit = 6,
  boxWidth = 48,
  boxHeight = 48,
  spacing = 16,
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  style = undefined,
  onDigitEnd = null,
  updateVia = undefined, 
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  ...others
}){

  const ref = useRef([]);
  const [innerFormValue, setInnerFormValue] = useState({});

  useEffect(() => {
    let _value = Accessor.Get(formValue, name) ?? "";
    if(_value === ""){
      ref?.current[0]?.focus?.();
    }
    let _form = {};
    _.map(_.range(0, digit), o => {
      _form[`digit-${o}`] = _value[o] ?? "";
    });
    setInnerFormValue(_form);
    }, [formValue, digit, name]);

  if(_.isUndefined(name)) return null;

  const _onChangeTextForOne = (text, idx) => {
    innerFormValue[`digit-${idx}`] = text;
    setInnerFormValue(innerFormValue);
    let str = _.map(_.range(0, digit), o => innerFormValue[`digit-${o}`]).join("");
    _onChange(name, str);
  };

  const _deleteOne = (idx) => {
    if(_.isEmpty(innerFormValue[`digit-${idx}`])){
      idx--;
    }
    for(let i = idx; i <= digit; i ++){
      innerFormValue[`digit-${idx}`] = i+1 > digit ? "" : innerFormValue[`digit-${idx+1}`];
    }
    setInnerFormValue(innerFormValue);
    let str = _.map(_.range(0, digit), o => innerFormValue[`digit-${o}`]).join("");
    _onChange(name, str);
    ref?.current?.[Math.max(0, idx - 1)]?.focus?.();
  };

  const _nextFocus = (idx) => {
    if(idx + 1 < digit){
      ref?.current?.[idx + 1]?.focus?.();
    }else{
      let str = _.map(_.range(0, digit), o => innerFormValue[`digit-${o}`]).join("");
      onDigitEnd?.(str);
    }
  };

  const _checkPrev = (idx) => {
    _.map(_.range(0, idx), o => {
      if(_.isEmpty(innerFormValue[`digit-${o}`])){
        ref?.current[o]?.focus?.();
      }
    });
  };

  return (
    <HStack spacing={spacing}>
      {_.map(_.range(0, digit), o => {
        return (
          <XInput 
            value={innerFormValue[`digit-${o}`]}
            isControlled
            forceValueSetting
            forwardRef={e => {
              ref.current[o] = e;
            }}
            maxLength={1}
            onDeleteKeyDown={() => _deleteOne(o)}
            onKeyUp={() => _nextFocus(o)}
            onFocus={() => _checkPrev(o)}
            {...others} key={o} width={boxWidth} height={boxHeight} p={0}
            style={{
              ...style,
              textAlign: "center"
            }}
            onChangeText={(text) => {
              _onChangeTextForOne(text, o);
            }}
            />
        );
      })}
    </HStack>
  );
}

export default QFDigitBox;